/**
 * Client message ids.
 *
 * This is the idempotency key on a send (P2-14), which makes its only real
 * requirement collision resistance across everything a single conversation ever
 * sees. It must NOT be sequential and must not encode anything: it travels to the
 * server and comes back on the message, so a counter would tell an observer how
 * many messages this visitor has sent, and a timestamp would tell them when.
 */

/**
 * `crypto.randomUUID` needs a secure context. `https://widget.<domain>` always is
 * one and so is `http://localhost`, but a self-hosted deployment on plain http
 * is not, and there the method is simply absent — a `TypeError` at the moment the
 * visitor presses send. `getRandomValues` has no such requirement, so it is the
 * fallback rather than `Math.random`, which is neither unpredictable nor
 * collision-safe.
 *
 * The property access goes through an explicit optional type because the DOM lib
 * declares `randomUUID` as always present; a plain `typeof` check would be
 * flagged as an unnecessary condition and removed by the next person to read it.
 */
export function newClientMessageId(): string {
  const { randomUUID } = crypto as { randomUUID?: () => string };
  if (randomUUID !== undefined) {
    return randomUUID.call(crypto);
  }

  const bytes = crypto.getRandomValues(new Uint8Array(16));
  let hex = '';
  for (const byte of bytes) {
    hex += byte.toString(16).padStart(2, '0');
  }
  return hex;
}
